import { useEffect, useRef, useState } from "react";

/* Live worldwide place search via OpenStreetMap's free Nominatim geocoder.
   Unlike the bundled city list / PIN code data (still used for the instant
   PIN shortcut and the "near X" label), this sends the user's typed query
   text to a third-party server on every search — a deliberate, disclosed
   step beyond the map tiles (which only ever request images), agreed to
   explicitly since it's the only way to cover any place name, not just a
   ~200-entry bundled list. No API key/account needed; requests are
   debounced client-side (see useLiveSearch) to stay within Nominatim's
   public fair-use limits. */
const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";

export async function searchPlacesLive(query, limit = 8) {
  const q = String(query || "").trim();
  if (!q) return [];
  try {
    const url = `${NOMINATIM_URL}?format=json&q=${encodeURIComponent(q)}&limit=${limit}&addressdetails=0`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return data.map((r) => ({
      label: r.display_name,
      lat: parseFloat(r.lat),
      lon: parseFloat(r.lon),
    }));
  } catch {
    return [];
  }
}

// Rough timezone estimate from longitude alone (15° per hour of UTC offset,
// rounded to the nearest 15 minutes). There's no timezone-boundary lookup
// bundled in this app, so this is an approximation — same tradeoff already
// accepted for map-tapped and GPS coordinates elsewhere, which don't do a
// real timezone lookup either. Good enough for Panchangam purposes.
export function estimateTzOffsetMin(lon) {
  return Math.round((lon / 15) * 4) * 15;
}

// Debounced live search hook shared by every search box in the app. Waits
// for the user to pause typing (and requires a minimum query length) before
// firing a request, and ignores any response that arrives after a newer
// request has already been sent (avoids stale results flashing in).
export function useLiveSearch(query, { minLength = 3, debounceMs = 450 } = {}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < minLength) {
      reqIdRef.current += 1;
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      const myReqId = ++reqIdRef.current;
      const hits = await searchPlacesLive(q);
      if (myReqId === reqIdRef.current) {
        setResults(hits);
        setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [query, minLength, debounceMs]);

  return { results, loading };
}
